import React from 'react';

import { AttrsHelper } from '../../../sb-helpers/attrs-helper';
import { Image } from '../Image/Image';

export type AttachmentPreviewProps = {
  /** What the picture is of: its alt text, and the name a caller usually shows beside it. */
  fileName: string;
  name?: string;
  /**
   * Drawn when there is no picture, or when the bytes arrived and would not decode - usually an
   * `AttachmentBadge`, which is what a file with no picture of its own gets.
   */
  fallback?: React.ReactNode;
  /** The same failure `fallback` already answers, for a caller that has to act on it too. */
  onError?: () => void;
  /**
   * How much room the picture takes. `small` is the square an `AttachmentBadge` fills, so the two
   * line up in a list; `medium` and `large` are bigger squares for a picture worth looking at.
   *
   * **`auto` drops the square altogether** - the picture keeps its own shape and its own size, held
   * only to the width it is given. For the one place a picture is the subject rather than an entry
   * in a list, where cropping it to a square would be the wrong answer.
   */
  size?: 'small' | 'medium' | 'large' | 'auto';
  src?: string | null;
  title?: string;
};

/**
 * A sent picture at thumbnail size. **Cropped to a square deliberately**: a row of these is a row of
 * equal tiles or it is a ragged edge, and the whole picture is one click away wherever the caller
 * chooses to put it.
 *
 * `size="auto"` is the exception, for a picture that is the subject rather than an entry in a list.
 */
export const AttachmentPreview = ({
  fallback = null,
  fileName,
  name,
  onError,
  size = 'small',
  src,
  title,
}: AttachmentPreviewProps) => (
  <Image
    alt={fileName}
    className={AttrsHelper.formatClassname(
      'attachment-preview',
      size !== 'small' && `attachment-preview-${size}`
    )}
    fallback={fallback}
    name={name}
    onError={onError}
    src={src}
    title={title}
  />
);
